/**
 * Tooltip — la etiqueta flotante del design system.
 *
 * Portado de `clobi-design-system@0.1.0`. No usa librería de posicionamiento:
 * `computePosition` son veinte líneas y evita sumar `@floating-ui` al bundle.
 *
 * ⚠️ El disparador tiene que ser **un único elemento** que acepte `ref` y props
 * de evento — el componente lo clona para engancharse. Un fragmento o un texto
 * suelto no sirven.
 *
 * ⚠️ Y no reemplaza al `title` nativo en todos los casos: esto aparece con el
 * mouse y con el foco, pero en una pantalla táctil no hay ninguno de los dos.
 * Lo que sea indispensable entender va en la pantalla, no acá.
 *
 * 🔴 **Se renderiza en `document.body`, por portal.** Es `position: fixed`, y un
 * `fixed` se ubica respecto de la ventana SALVO que algún ancestro tenga
 * `transform`, `filter` o `backdrop-filter`: entonces ese ancestro pasa a ser
 * su bloque contenedor y las coordenadas de `getBoundingClientRect()` quedan
 * desplazadas. Las superficies de vidrio llevan `backdrop-filter`, así que sin
 * el portal el tooltip salía corrido en casi cualquier panel. Es el mismo
 * defecto que el #334 cerró para los desplegables. Así venía del sistema; se
 * corrigió antes de su primer uso (2026-09-30).
 */
import { cloneElement, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactElement, ReactNode } from 'react'

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

const OPUESTO: Record<TooltipSide, TooltipSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

/** Separación con el disparador y margen mínimo contra el borde de la ventana. */
const SEPARACION = 8
const MARGEN = 8

interface Posicion {
  top: number
  left: number
  side: TooltipSide
}

/**
 * Elige el lado y las coordenadas. Si el preferido no entra prueba el opuesto, y
 * después acota la posición a la ventana: un tooltip que se sale del viewport no
 * se lee, y es justo el caso del último elemento de una tabla.
 */
function calcularPosicion(
  disparador: DOMRect,
  tooltip: DOMRect,
  preferido: TooltipSide,
): Posicion {
  const entra = (lado: TooltipSide) => {
    if (lado === 'top') return disparador.top - tooltip.height - SEPARACION >= 0
    if (lado === 'bottom') return disparador.bottom + tooltip.height + SEPARACION <= window.innerHeight
    if (lado === 'left') return disparador.left - tooltip.width - SEPARACION >= 0
    return disparador.right + tooltip.width + SEPARACION <= window.innerWidth
  }

  const lado = entra(preferido) ? preferido : entra(OPUESTO[preferido]) ? OPUESTO[preferido] : preferido

  let top: number
  let left: number
  if (lado === 'top' || lado === 'bottom') {
    top = lado === 'top' ? disparador.top - tooltip.height - SEPARACION : disparador.bottom + SEPARACION
    left = disparador.left + disparador.width / 2 - tooltip.width / 2
  } else {
    left = lado === 'left' ? disparador.left - tooltip.width - SEPARACION : disparador.right + SEPARACION
    top = disparador.top + disparador.height / 2 - tooltip.height / 2
  }

  left = Math.min(Math.max(left, MARGEN), window.innerWidth - tooltip.width - MARGEN)
  top = Math.min(Math.max(top, MARGEN), window.innerHeight - tooltip.height - MARGEN)
  return { top, left, side: lado }
}

export interface TooltipProps {
  content: ReactNode
  /** Por defecto `'top'`. Voltea al lado opuesto si no cabe. */
  side?: TooltipSide
  /** Retardo de apertura en ms. Por defecto 400. */
  delay?: number
  /** El disparador: un único elemento que acepte `ref` y props de evento. */
  children: ReactElement
  className?: string
}

export function Tooltip({ content, side = 'top', delay = 400, children, className = '' }: TooltipProps) {
  const id = useId()
  const [abierto, setAbierto] = useState(false)
  const [pos, setPos] = useState<Posicion | null>(null)
  const refDisparador = useRef<HTMLElement | null>(null)
  const refTooltip = useRef<HTMLDivElement | null>(null)
  const temporizador = useRef<number | undefined>(undefined)

  const mostrar = () => {
    temporizador.current = window.setTimeout(() => setAbierto(true), delay)
  }
  const ocultar = () => {
    window.clearTimeout(temporizador.current)
    setAbierto(false)
  }

  // `useLayoutEffect` y no `useEffect`: hay que medir y colocar ANTES de pintar,
  // o el tooltip se ve un frame en la esquina y salta a su lugar.
  useLayoutEffect(() => {
    if (!abierto || !refDisparador.current || !refTooltip.current) return
    setPos(calcularPosicion(
      refDisparador.current.getBoundingClientRect(),
      refTooltip.current.getBoundingClientRect(),
      side,
    ))
  }, [abierto, side])

  useLayoutEffect(() => () => window.clearTimeout(temporizador.current), [])

  const props = children.props as Record<string, unknown>
  const llamar = (nombre: string, e: unknown) => {
    const fn = props[nombre]
    if (typeof fn === 'function') (fn as (ev: unknown) => void)(e)
  }

  const disparador = cloneElement(children, {
    // La ref propia se encadena con la que el hijo ya tuviera: pisarla dejaría
    // sin funcionar a quien la estuviera usando.
    ref: (node: HTMLElement | null) => {
      refDisparador.current = node
      const suya = (children as unknown as { ref?: unknown }).ref
      if (typeof suya === 'function') suya(node)
      else if (suya && typeof suya === 'object') (suya as { current: unknown }).current = node
    },
    'aria-describedby': abierto ? id : props['aria-describedby'],
    onMouseEnter: (e: unknown) => { mostrar(); llamar('onMouseEnter', e) },
    onMouseLeave: (e: unknown) => { ocultar(); llamar('onMouseLeave', e) },
    onFocus: (e: unknown) => { mostrar(); llamar('onFocus', e) },
    onBlur: (e: unknown) => { ocultar(); llamar('onBlur', e) },
    onKeyDown: (e: { key?: string }) => {
      if (e?.key === 'Escape') ocultar()
      llamar('onKeyDown', e)
    },
  } as never)

  return (
    <>
      {disparador}
      {abierto && createPortal(
        <div
          ref={refTooltip}
          role="tooltip"
          id={id}
          className={`cl-tooltip ${className}`.trim()}
          // Fuera de pantalla hasta tener la medida: así el primer frame no lo
          // muestra en una posición equivocada.
          style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  )
}
